import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { Loader2, Check, Sparkles, ArrowLeft, Zap } from "lucide-react";
import { Button } from "../../shared/ui";
import { stagger, item } from "../../shared/motion/transitions";
import { listPlans, getBilling, createCheckout, type BillingPlan } from "../../features/billing/billingApi";

const money = (n: number, c = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n);

export default function ChoosePlanPage() {
  const [plans, setPlans] = useState<BillingPlan[] | null>(null);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [interval, setInterval] = useState<"month" | "year">("month");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([listPlans(), getBilling().catch(() => null)]).then(([p, b]) => {
      setPlans(p);
      setCurrentPlanId(b?.plan?._id ?? null);
    }).catch((e) => setError(e instanceof Error ? e.message : "Failed to load plans"));
  }, []);

  const subscribe = async (plan: BillingPlan) => {
    setBusyId(plan._id); setError(null);
    try { const { checkoutUrl } = await createCheckout(plan._id); window.location.assign(checkoutUrl); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not start checkout"); setBusyId(null); }
  };

  const priceFor = (p: BillingPlan) => (interval === "year" ? Math.round(p.annualPrice / 12) : p.monthlyPrice);

  return (
    <div className="mx-auto w-full max-w-[1080px] px-4 py-6 sm:px-6 sm:py-8">
      <Link to="/billing" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-500 hover:text-ink-800"><ArrowLeft size={14} /> Billing</Link>

      {/* Hero */}
      <div className="relative mt-4 overflow-hidden rounded-3xl border border-ink-100 bg-gradient-to-br from-gold-50 to-emerald/5 p-8 sm:p-10">
        <Sparkles size={20} className="absolute right-6 top-6 text-gold-400" />
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold-700">Plans</p>
        <h1 className="mt-2 font-headline text-[28px] leading-tight text-ink-900 sm:text-[36px]">Pick the right plan for your studio</h1>
        <p className="mt-2 max-w-[520px] text-[14px] text-ink-600">Every plan includes the 3D viewer, configurators, and embeds. Upgrade or downgrade anytime.</p>

        {/* Interval toggle */}
        <div className="mt-5 inline-flex rounded-xl border border-ink-200 bg-white/70 p-0.5 text-[12.5px]">
          {(["month", "year"] as const).map((iv) => (
            <button key={iv} onClick={() => setInterval(iv)} className={`rounded-lg px-4 py-1.5 font-medium ${interval === iv ? "bg-white text-ink-900 shadow-sm" : "text-ink-500"}`}>
              {iv === "month" ? "Monthly" : "Annual · save ~17%"}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="mt-4 rounded-lg bg-rose/10 px-3 py-2 text-[12.5px] text-rose">{error}</p>}

      {!plans ? (
        <div className="flex h-48 items-center justify-center text-ink-400"><Loader2 className="animate-spin" /></div>
      ) : (
        <motion.div variants={stagger} initial="hidden" animate="show" className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {plans.map((p) => {
            const isCurrent = p._id === currentPlanId;
            const price = priceFor(p);
            return (
              <motion.div
                key={p._id}
                variants={item}
                className={`relative flex flex-col rounded-2xl border bg-white p-6 transition-all hover:-translate-y-0.5 hover:shadow-pop ${p.popular ? "border-gold-300 shadow-card" : "border-ink-100"}`}
              >
                {p.popular && <span className="absolute -top-2.5 left-6 rounded-full bg-gradient-to-r from-gold-500 to-gold-400 px-2.5 py-0.5 text-[10px] font-semibold text-white">Most popular</span>}
                {isCurrent && <span className="absolute -top-2.5 right-6 rounded-full bg-emerald px-2.5 py-0.5 text-[10px] font-semibold text-white">Current</span>}
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">{p.name}</p>
                <p className="mt-2 font-headline text-[30px] text-ink-900">{price > 0 ? money(price) : "Free"}<span className="text-[13px] font-normal text-ink-400">{price > 0 ? "/mo" : ""}</span></p>
                {interval === "year" && p.annualPrice > 0 && <p className="text-[11.5px] text-ink-400">billed annually</p>}
                {p.description && <p className="mt-2 text-[12.5px] text-ink-500">{p.description}</p>}

                <ul className="mt-4 flex-1 space-y-2 text-[12.5px] text-ink-600">
                  <li className="flex items-center gap-2"><Zap size={14} className="text-gold-500" /> {p.limits.maxModels < 0 ? "Unlimited" : p.limits.maxModels} models</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-emerald" /> {p.limits.maxConfigurators < 0 ? "Unlimited" : p.limits.maxConfigurators} configurators</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-emerald" /> {p.limits.maxDomains < 0 ? "Unlimited" : p.limits.maxDomains} custom domain{p.limits.maxDomains === 1 ? "" : "s"}</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-emerald" /> {p.maxMembers} team seat{p.maxMembers > 1 ? "s" : ""}</li>
                  {p.features.slice(0, 3).map((f) => <li key={f} className="flex items-center gap-2"><Check size={14} className="text-emerald" /> {f}</li>)}
                </ul>

                <Button
                  variant={isCurrent ? "secondary" : p.popular ? "gold" : "primary"}
                  size="sm"
                  className="mt-5 w-full justify-center"
                  disabled={isCurrent || busyId === p._id || p.monthlyPrice === 0}
                  onClick={() => subscribe(p)}
                >
                  {busyId === p._id ? <Loader2 size={14} className="animate-spin" /> : null}
                  {isCurrent ? "Current plan" : p.monthlyPrice === 0 ? "Free" : `Subscribe · ${money(price)}/mo`}
                </Button>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
